import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useLanguage } from '../../../contexts/LanguageContext';

const NotificationSchedulePanel = ({ scheduledNotifications, onEditSchedule, onCancelSchedule }) => {
  const { t, isRTL } = useLanguage();

  const notificationTypeConfig = {
    email: {
      icon: 'Mail',
      color: 'text-primary',
      label: t('visas.email')
    },
    sms: {
      icon: 'MessageSquare',
      color: 'text-accent',
      label: t('visas.sms')
    },
    whatsapp: {
      icon: 'MessageCircle',
      color: 'text-success',
      label: t('visas.whatsapp')
    }
  };

  const channelConfig = {
    email: {
      icon: 'Mail',
      color: 'text-primary',
      label: t('visas.email')
    },
    sms: {
      icon: 'MessageSquare',
      color: 'text-warning',
      label: t('visas.sms')
    },
    whatsapp: {
      icon: 'MessageCircle',
      color: 'text-success',
      label: t('visas.whatsapp')
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    // Always use en-US locale for universal date/time format (Western numerals)
    return date?.toLocaleString('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card rounded-lg border border-border p-4 md:p-6 shadow-warm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
            <Icon name="Bell" size={20} className="text-accent" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">{t('visas.scheduledNotifications')}</h3>
            <p className="caption text-muted-foreground">
              {scheduledNotifications?.length} {scheduledNotifications?.length !== 1 ? t('visas.notificationsPlural') : t('visas.notificationsSingular')} {t('visas.waiting')}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="Plus"
          iconPosition="left"
        >
          {t('visas.schedule')}
        </Button>
      </div>
      <div className="space-y-3">
        {scheduledNotifications?.map((notification) => {
          const typeConfig = notificationTypeConfig?.[notification?.type] || notificationTypeConfig?.email;
          
          return (
            <div key={notification?.id} className="p-4 bg-muted/30 rounded-lg border border-border">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <div className={`w-8 h-8 rounded-full bg-${typeConfig?.color?.split('-')?.[1]}/10 flex items-center justify-center flex-shrink-0`}>
                    <Icon name={typeConfig?.icon} size={16} className={typeConfig?.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-foreground">{notification?.template}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-md ${typeConfig?.color} bg-${typeConfig?.color?.split('-')?.[1]}/10`}>
                        {typeConfig?.label}
                      </span>
                    </div>
                    <p className="caption text-muted-foreground mb-2">
                      {notification?.recipientCount} {notification?.recipientCount !== 1 ? t('visas.recipientsPlural') : t('visas.recipientsSingular')}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon name="Clock" size={12} />
                      <span>{t('visas.scheduledFor')} {formatDateTime(notification?.scheduledTime)}</span>
                    </div>
                    {notification?.language && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                        <Icon name="Languages" size={12} />
                        <span>{notification?.language === 'fr' ? t('visas.french') : t('visas.arabic')}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Edit"
                    onClick={() => onEditSchedule(notification?.id)}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Trash2"
                    onClick={() => onCancelSchedule(notification?.id)}
                  />
                </div>
              </div>
            </div>
          );
        })}

        {scheduledNotifications?.length === 0 && (
          <div className="text-center py-8">
            <Icon name="BellOff" size={48} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">{t('visas.noScheduledNotifications')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationSchedulePanel;